import { useEffect, useId, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  File as FileIcon,
  Folder,
  FolderInput,
  FolderOpen,
  Loader2,
  Paperclip,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import type { JSONSchema } from '@/lib/types';
import type { FieldLocale } from '@/lib/i18n/commands';
import {
  localFsApi,
  type LocalBrowseEntry,
  type LocalBrowseListing,
  type LocalBrowseRoot,
} from '@/lib/api';
import clsx from 'clsx';

/* ------------------------------------------------------------------------ */
/* Schema resolution (recursive, $ref-aware)                                 */
/* ------------------------------------------------------------------------ */

/**
 * Walk into a {@code $ref}-resolving wrapper. The Java SDK's
 * {@code SchemaGen} emits nested POJOs as {@code $ref}-based
 * {@code $defs} entries — the renderer below resolves them lazily so the
 * recursive form keeps working regardless of inline-vs-ref layout.
 */
function resolveSchema(
  schema: JSONSchema | undefined,
  rootSchema: JSONSchema
): JSONSchema | undefined {
  if (!schema) return schema;
  const ref = schema.$ref;
  if (!ref) return schema;
  // Only support local $refs of the form `#/$defs/Foo` (or legacy
  // `#/definitions/Foo`) — that's what victools-generated schemas use.
  if (!ref.startsWith('#/')) return schema;
  const parts = ref.slice(2).split('/');
  let cur: unknown = rootSchema;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return schema;
    }
  }
  const resolved = cur as JSONSchema | undefined;
  // Recurse one more time in case the $def itself is another $ref.
  return resolveSchema(resolved, rootSchema);
}

function isObjectSchema(s: JSONSchema | undefined): boolean {
  if (!s) return false;
  if (s.type === 'object') return true;
  if (Array.isArray(s.type) && s.type.includes('object')) return true;
  // Treat unspecified-type schemas with a `properties` map as object too —
  // some generators omit the explicit type when properties are present.
  return !!s.properties;
}

function isArraySchema(s: JSONSchema | undefined): boolean {
  if (!s) return false;
  if (s.type === 'array') return true;
  if (Array.isArray(s.type) && s.type.includes('array')) return true;
  return false;
}

/* ------------------------------------------------------------------------ */
/* Top-level component                                                      */
/* ------------------------------------------------------------------------ */

interface Props {
  schema: JSONSchema | undefined;
  value: Record<string, unknown> | unknown;
  onChange: (v: Record<string, unknown> | unknown) => void;
  example?: Record<string, unknown>;
  /** Per-field Chinese labels / hints / presets keyed by field name. */
  fieldLocale?: Record<string, FieldLocale>;
  /** When false the inputs are rendered as read-only text. */
  editable?: boolean;
}

/**
 * Recursive JSON Schema → React form. Handles:
 *
 * <ul>
 *   <li>scalars (string / number / integer / boolean / enum)
 *   <li>nested {@code object}s (inline or via {@code $ref} / {@code $defs})
 *   <li>arrays of scalars (one-per-line textarea)
 *   <li>arrays of objects ({@code +} / {@code −} card stack)
 *   <li>required-field validation hints (bubbled up via the {@code *}
 *       markers — actual blocking lives in {@code CmdRunCard})
 *   <li>the {@code xInput: 'file'} extension's file-browser modal
 * </ul>
 */
export function JsonSchemaForm({
  schema,
  value,
  onChange,
  example,
  fieldLocale,
  editable = true,
}: Props): JSX.Element {
  const rootSchema = schema ?? ({} as JSONSchema);
  const resolved = useMemo(() => resolveSchema(schema, rootSchema), [schema, rootSchema]);

  /* One-shot seeding: example payload + defaults walked recursively. */
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !resolved) return;
    if (!isObjectSchema(resolved)) {
      setSeeded(true);
      return;
    }
    const props = resolved.properties ?? {};
    const exampleClean: Record<string, unknown> = { ...(example ?? {}) };
    for (const [k, sub] of Object.entries(props)) {
      const r = resolveSchema(sub, rootSchema);
      if (r?.xInput === 'file') delete exampleClean[k];
    }
    const current = (value as Record<string, unknown>) ?? {};
    const init: Record<string, unknown> = { ...exampleClean, ...current };
    for (const [k, sub] of Object.entries(props)) {
      const r = resolveSchema(sub, rootSchema);
      if (r?.xInput === 'file') continue;
      if (init[k] === undefined && r?.default !== undefined) init[k] = r.default;
    }
    onChange(init);
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, seeded]);

  if (!resolved) {
    return (
      <p className="text-xs text-ink-muted">
        无参数，直接点 <span className="kbd">Run</span> 即可。
      </p>
    );
  }

  if (!isObjectSchema(resolved)) {
    return (
      <p className="text-xs text-ink-muted">
        该命令的入参不是 object，无法渲染表单。期望 args 是 JSON 对象。
      </p>
    );
  }

  const properties = resolved.properties ?? {};
  if (Object.keys(properties).length === 0) {
    return (
      <p className="text-xs text-ink-muted">
        无参数，直接点 <span className="kbd">Run</span> 即可。
      </p>
    );
  }

  return (
    <ObjectFields
      schema={resolved}
      value={(value as Record<string, unknown>) ?? {}}
      onChange={(v) => onChange(v)}
      rootSchema={rootSchema}
      fieldLocale={fieldLocale}
      editable={editable}
    />
  );
}

/* ------------------------------------------------------------------------ */
/* Object-shape renderer                                                    */
/* ------------------------------------------------------------------------ */

function ObjectFields({
  schema,
  value,
  onChange,
  rootSchema,
  fieldLocale,
  editable,
  pathLabel,
}: {
  schema: JSONSchema;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  rootSchema: JSONSchema;
  fieldLocale?: Record<string, FieldLocale>;
  editable: boolean;
  pathLabel?: string;
}): JSX.Element {
  const properties = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const setKey = (k: string, v: unknown) => {
    const next = { ...value, [k]: v };
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {pathLabel && (
        <p className="text-[10px] uppercase tracking-wide text-ink-dim">{pathLabel}</p>
      )}
      {Object.entries(properties).map(([k, sub]) => (
        <FieldRow
          key={k}
          name={k}
          required={required.has(k)}
          schema={sub}
          rootSchema={rootSchema}
          locale={fieldLocale?.[k]}
          value={value[k]}
          onChange={(v) => setKey(k, v)}
          editable={editable}
        />
      ))}
    </div>
  );
}

function FieldRow({
  name,
  schema,
  rootSchema,
  locale,
  value,
  onChange,
  required,
  editable,
}: {
  name: string;
  schema: JSONSchema;
  rootSchema: JSONSchema;
  locale?: FieldLocale;
  value: unknown;
  onChange: (v: unknown) => void;
  required: boolean;
  editable: boolean;
}) {
  const resolved = resolveSchema(schema, rootSchema) ?? schema;
  const label = locale?.label ?? resolved.title ?? name;
  const description = locale?.hint ?? resolved.description;
  return (
    <label className="block text-sm">
      <div className="mb-1 flex items-center gap-2">
        <span className="font-medium text-ink">{label}</span>
        <span className="text-[11px] text-ink-dim">{name}</span>
        {required && <span className="text-[11px] text-rose-500">必填</span>}
      </div>
      <Field
        schema={resolved}
        rootSchema={rootSchema}
        value={value}
        onChange={onChange}
        locale={locale}
        editable={editable}
      />
      {description && (
        <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-ink-muted">
          {description}
        </p>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------------ */
/* Field dispatcher                                                          */
/* ------------------------------------------------------------------------ */

function Field({
  schema,
  rootSchema,
  value,
  onChange,
  locale,
  editable,
}: {
  schema: JSONSchema;
  rootSchema: JSONSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  locale?: FieldLocale;
  editable: boolean;
}) {
  const presets = locale?.presets ?? [];
  const placeholder = locale?.placeholder ?? (schema.examples ? String(schema.examples[0] ?? '') : '');
  const listId = useId();

  if (schema.xInput === 'file') {
    return <FileField schema={schema} value={value} onChange={onChange} editable={editable} />;
  }

  if (schema.enum) {
    if (!editable) {
      return <ReadOnlyView value={value} />;
    }
    return (
      <select
        className="input w-full"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">--</option>
        {schema.enum.map((v) => (
          <option key={String(v)} value={String(v)}>
            {String(v)}
          </option>
        ))}
      </select>
    );
  }

  if (schema.type === 'boolean') {
    if (!editable) {
      return <ReadOnlyView value={value} />;
    }
    return (
      <label className="inline-flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-xs text-ink-muted">{Boolean(value) ? 'true' : 'false'}</span>
      </label>
    );
  }

  if (schema.type === 'integer' || schema.type === 'number') {
    if (!editable) {
      return <ReadOnlyView value={value} />;
    }
    return (
      <>
        <input
          className="input w-full"
          type="number"
          value={value === undefined || value === null ? '' : (value as number)}
          min={schema.minimum}
          max={schema.maximum}
          list={presets.length > 0 ? listId : undefined}
          placeholder={placeholder}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') return onChange(undefined);
            onChange(schema.type === 'integer' ? parseInt(v, 10) : parseFloat(v));
          }}
        />
        {presets.length > 0 && (
          <datalist id={listId}>
            {presets.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        )}
      </>
    );
  }

  if (isArraySchema(schema)) {
    return (
      <ArrayField
        schema={schema}
        rootSchema={rootSchema}
        value={value}
        onChange={onChange}
        editable={editable}
      />
    );
  }

  if (isObjectSchema(schema)) {
    return (
      <NestedObjectField
        schema={schema}
        rootSchema={rootSchema}
        value={value}
        onChange={onChange}
        editable={editable}
      />
    );
  }

  if (!editable) {
    return <ReadOnlyView value={value} />;
  }

  return (
    <>
      <input
        className="input w-full"
        type="text"
        value={(value as string) ?? ''}
        placeholder={placeholder}
        list={presets.length > 0 ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {presets.length > 0 && (
        <datalist id={listId}>
          {presets.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Nested object — collapsible sub-form                                      */
/* ------------------------------------------------------------------------ */

function NestedObjectField({
  schema,
  rootSchema,
  value,
  onChange,
  editable,
}: {
  schema: JSONSchema;
  rootSchema: JSONSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  editable: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  const obj = (value as Record<string, unknown>) ?? {};
  return (
    <div className="rounded border border-line bg-bg-soft/40 p-2">
      <button
        type="button"
        className="mb-1 flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {schema.title ?? 'object'}
      </button>
      {open && (
        <ObjectFields
          schema={schema}
          value={obj}
          onChange={(v) => onChange(v)}
          rootSchema={rootSchema}
          editable={editable}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Array field — scalar list, primitive list, or object card stack           */
/* ------------------------------------------------------------------------ */

function ArrayField({
  schema,
  rootSchema,
  value,
  onChange,
  editable,
}: {
  schema: JSONSchema;
  rootSchema: JSONSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  editable: boolean;
}): JSX.Element {
  const itemSchemaRaw = Array.isArray(schema.items) ? schema.items[0] : schema.items;
  const itemSchema = resolveSchema(itemSchemaRaw, rootSchema);
  const arr = Array.isArray(value) ? (value as unknown[]) : [];

  if (itemSchema && isObjectSchema(itemSchema)) {
    return (
      <ArrayOfObjects
        items={arr}
        itemSchema={itemSchema}
        rootSchema={rootSchema}
        onChange={onChange}
        editable={editable}
      />
    );
  }

  // Scalar items — use the simpler one-per-line textarea but type-coerce
  // back to the declared scalar type so the wire is correct.
  const coerce = (s: string): unknown => {
    const t = itemSchema?.type;
    if (t === 'integer') return parseInt(s, 10);
    if (t === 'number') return parseFloat(s);
    if (t === 'boolean') return s.toLowerCase() === 'true';
    return s;
  };
  if (!editable) {
    return <ReadOnlyView value={value} />;
  }
  return (
    <textarea
      className="input w-full font-mono text-xs"
      rows={2}
      value={arr.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n')}
      onChange={(e) =>
        onChange(
          e.target.value
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map(coerce)
        )
      }
      placeholder="一行一个"
    />
  );
}

function ArrayOfObjects({
  items,
  itemSchema,
  rootSchema,
  onChange,
  editable,
}: {
  items: unknown[];
  itemSchema: JSONSchema;
  rootSchema: JSONSchema;
  onChange: (v: unknown) => void;
  editable: boolean;
}): JSX.Element {
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="rounded border border-line p-2">
          <div className="mb-1 flex items-center justify-between text-[11px] text-ink-dim">
            <span>#{idx + 1}</span>
            {editable && (
              <button
                type="button"
                className="rounded p-0.5 text-ink-dim hover:bg-rose-500/10 hover:text-rose-500"
                onClick={() => onChange(items.filter((_, i) => i !== idx))}
                title="移除"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
          <ObjectFields
            schema={itemSchema}
            value={(item as Record<string, unknown>) ?? {}}
            onChange={(v) => onChange(items.map((x, i) => (i === idx ? v : x)))}
            rootSchema={rootSchema}
            editable={editable}
          />
        </div>
      ))}
      {editable && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded border border-dashed border-line px-2 py-1 text-[11px] text-ink-muted transition hover:border-accent hover:text-accent"
          onClick={() => onChange([...items, {}])}
        >
          <Plus className="h-3 w-3" /> 添加项
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Read-only fallback used by the result view                                */
/* ------------------------------------------------------------------------ */

function ReadOnlyView({ value }: { value: unknown }): JSX.Element {
  if (value === undefined || value === null) {
    return <span className="text-xs text-ink-dim">—</span>;
  }
  if (typeof value === 'object') {
    return (
      <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-1 font-mono text-[11px]">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span className="font-mono text-xs text-ink">{String(value)}</span>;
}

/* ------------------------------------------------------------------------ */
/* xInput: 'file' picker (unchanged from the prior implementation)           */
/* ------------------------------------------------------------------------ */

function FileField({
  schema,
  value,
  onChange,
  editable,
}: {
  schema: JSONSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  editable: boolean;
}) {
  const [open, setOpen] = useState(false);
  const path = typeof value === 'string' ? value : '';

  const clear = (): void => {
    onChange('');
  };

  if (!editable) {
    return <ReadOnlyView value={value} />;
  }
  return (
    <div className="space-y-1.5">
      {!path && (
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-dashed border-line bg-bg-soft px-3 py-2 text-sm text-ink-muted transition hover:border-accent hover:bg-accent-soft hover:text-accent"
          onClick={() => setOpen(true)}
        >
          <FolderOpen className="h-4 w-4" />
          <span>{schema.xAccept ? `选择 ${schema.xAccept} 文件` : '选择文件'}</span>
        </button>
      )}
      {path && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-bg-soft px-3 py-2 text-xs">
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          <span className="min-w-0 flex-1 truncate font-medium text-ink" title={path}>
            {path.split('/').pop() ?? path}
          </span>
          <span className="hidden truncate text-ink-dim sm:inline" title={path}>
            {path}
          </span>
          <button
            type="button"
            className="btn-ghost px-2 py-0.5 text-[11px]"
            onClick={() => setOpen(true)}
            title="重新选择"
          >
            替换
          </button>
          <button
            type="button"
            className="btn-ghost px-2 py-0.5 text-[11px] text-ink-dim hover:text-rose-500"
            onClick={clear}
            title="清除"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {open && (
        <FileBrowserModal
          accept={schema.xAccept}
          onClose={() => setOpen(false)}
          onPick={(picked) => {
            onChange(picked);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function FileBrowserModal({
  accept,
  onClose,
  onPick,
}: {
  accept?: string;
  onClose: () => void;
  onPick: (path: string) => void;
}) {
  const [roots, setRoots] = useState<LocalBrowseRoot[] | null>(null);
  const [listing, setListing] = useState<LocalBrowseListing | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [pickWarn, setPickWarn] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const acceptExts = useMemo(() => {
    if (!accept) return null;
    const list = accept
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.startsWith('.'));
    return list.length > 0 ? new Set(list) : null;
  }, [accept]);

  const navigate = async (target: string): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await localFsApi.browse(target);
      setListing(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { roots: rs } = await localFsApi.roots();
        if (cancelled) return;
        setRoots(rs);
        if (rs.length > 0) {
          await navigate(rs[0].path);
        } else {
          setLoading(false);
          setError('没有可用的浏览目录。可在 adh.config.json 里配置 localBrowseRoots。');
        }
      } catch (e) {
        if (cancelled) return;
        setLoading(false);
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const accepts = (entry: LocalBrowseEntry): boolean => {
    if (entry.isDir) return true;
    if (!acceptExts) return true;
    const lower = entry.name.toLowerCase();
    for (const ext of acceptExts) {
      if (lower.endsWith(ext)) return true;
    }
    return false;
  };

  const pickNative = async (): Promise<void> => {
    if (picking) return;
    setPicking(true);
    setPickWarn(null);
    try {
      const r = await localFsApi.nativePick({ accept });
      switch (r.status) {
        case 'picked':
          onPick(r.path);
          return;
        case 'cancelled':
          return;
        case 'unsupported':
          setPickWarn(
            `当前系统没有可用的文件选择对话框（${r.reason}）。请改用左侧的快捷目录。`
          );
          return;
        case 'error':
          setPickWarn(`无法弹出系统选择框：${r.reason}`);
          return;
      }
    } catch (e) {
      setPickWarn(e instanceof Error ? e.message : String(e));
    } finally {
      setPicking(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="card flex h-[70vh] max-h-[640px] w-full max-w-3xl flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-line px-3 py-2">
          <h3 className="text-sm font-medium text-ink">
            选择文件{accept ? ` · ${accept}` : ''}
          </h3>
          <div className="flex items-center gap-1">
            <button
              className="btn-ghost"
              onClick={() => listing && void navigate(listing.path)}
              disabled={!listing || loading}
              title="刷新当前目录"
            >
              <RefreshCw className={clsx('h-3.5 w-3.5', loading && 'animate-spin')} />
            </button>
            <button className="btn-ghost" onClick={onClose} title="关闭">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex min-h-0 flex-1">
          <aside className="w-40 shrink-0 overflow-auto border-r border-line bg-bg-soft py-2 text-sm">
            {roots?.map((r) => {
              const active = listing?.root.id === r.id;
              return (
                <button
                  key={r.id}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left transition',
                    active ? 'bg-accent-soft text-accent' : 'text-ink-muted hover:bg-bg hover:text-ink'
                  )}
                  onClick={() => void navigate(r.path)}
                >
                  <Folder className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{r.label}</span>
                </button>
              );
            })}
            {roots && roots.length === 0 && (
              <p className="px-3 py-2 text-[11px] text-ink-dim">无可用目录</p>
            )}
          </aside>
          <section className="flex min-w-0 flex-1 flex-col">
            <div className="border-b border-line px-3 py-2">
              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-md border border-accent bg-accent-soft px-3 py-1.5 text-xs font-medium text-accent transition hover:bg-accent hover:text-white disabled:cursor-wait disabled:opacity-60"
                disabled={picking}
                onClick={() => void pickNative()}
                title="弹出系统自带的文件选择对话框，可选电脑上任意位置的文件"
              >
                {picking ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FolderInput className="h-3.5 w-3.5" />
                )}
                <span>
                  {picking
                    ? '等待你在系统对话框选择…'
                    : '选择电脑上其他路径文件…'}
                </span>
              </button>
              {pickWarn && (
                <p className="mt-2 flex items-start gap-1 text-[11px] text-amber-500">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{pickWarn}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 overflow-auto border-b border-line px-3 py-1.5 text-[11px] text-ink-muted">
              {listing ? (
                <Breadcrumb listing={listing} onNavigate={(p) => void navigate(p)} />
              ) : (
                <span className="text-ink-dim">…</span>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              {loading && (
                <div className="flex h-full items-center justify-center text-xs text-ink-muted">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 加载中…
                </div>
              )}
              {!loading && error && (
                <div className="m-3 flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-500">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {!loading && !error && listing && (
                <ul className="divide-y divide-line/60 text-sm">
                  {listing.parent && (
                    <li>
                      <button
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-ink-muted hover:bg-bg-soft"
                        onClick={() => void navigate(listing.parent!)}
                      >
                        <Folder className="h-3.5 w-3.5" />
                        <span>..</span>
                      </button>
                    </li>
                  )}
                  {listing.entries.length === 0 && (
                    <li className="px-3 py-4 text-center text-xs text-ink-dim">
                      此目录为空
                    </li>
                  )}
                  {listing.entries.map((e) => {
                    const ok = accepts(e);
                    return (
                      <li key={e.path}>
                        <button
                          className={clsx(
                            'flex w-full items-center gap-2 px-3 py-1.5 text-left transition',
                            ok ? 'hover:bg-bg-soft' : 'cursor-not-allowed opacity-40'
                          )}
                          disabled={!ok}
                          onClick={() => (e.isDir ? void navigate(e.path) : onPick(e.path))}
                        >
                          {e.isDir ? (
                            <Folder className="h-3.5 w-3.5 text-amber-500" />
                          ) : (
                            <FileIcon className="h-3.5 w-3.5 text-ink-muted" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-ink">{e.name}</span>
                          {!e.isDir && (
                            <>
                              <span className="hidden text-[11px] text-ink-dim sm:inline">
                                {formatBytes(e.size)}
                              </span>
                              <span className="hidden text-[11px] text-ink-dim md:inline">
                                {new Date(e.mtime).toLocaleString()}
                              </span>
                            </>
                          )}
                          {e.isDir && <ChevronRight className="h-3 w-3 text-ink-dim" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Breadcrumb({
  listing,
  onNavigate,
}: {
  listing: LocalBrowseListing;
  onNavigate: (path: string) => void;
}) {
  const root = listing.root;
  const segments = useMemo(() => {
    if (listing.path === root.path) return [] as Array<{ name: string; path: string }>;
    const rel = listing.path.slice(root.path.length).replace(/^\//, '');
    const parts = rel.split('/').filter(Boolean);
    const out: Array<{ name: string; path: string }> = [];
    let acc = root.path;
    for (const p of parts) {
      acc = acc + '/' + p;
      out.push({ name: p, path: acc });
    }
    return out;
  }, [listing.path, root.path]);
  return (
    <div className="flex flex-wrap items-center gap-0.5">
      <button
        className="rounded px-1.5 py-0.5 font-medium text-ink hover:bg-bg-soft"
        onClick={() => onNavigate(root.path)}
      >
        {root.label}
      </button>
      {segments.map((s) => (
        <span key={s.path} className="inline-flex items-center gap-0.5">
          <ChevronRight className="h-3 w-3 text-ink-dim" />
          <button
            className="rounded px-1.5 py-0.5 hover:bg-bg-soft hover:text-ink"
            onClick={() => onNavigate(s.path)}
          >
            {s.name}
          </button>
        </span>
      ))}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
